import Vue from 'vue'
import modalTabControlMixin from 'mixins/modal-tab-control-mixin'
import msg from 'services/msg'
import domUtils from 'util/dom-utils'
import { disableBodyScroll, enableBodyScroll } from 'util/body-scroll-lock'
import Popover from 'objects/popover/popover'

import withRender from './modal.html'
import './modal.scss'

// NOTE: MUST USE v-if TO SHOW/HIDE THE MODAL. This is due to logic that adds an overflow style to
// the body of the page and removes that same style before the component is destroyed.
export default withRender((Vue as any).extend({
  data() { return { scrollPosition: 0 } },
  mixins: [modalTabControlMixin],
  props: {
    ariaLabel: String,
    ariaLabeledById: String,
    centered: {
      type: Boolean,
      default: false
    },
    closeLabel: {
      type: String,
      // had to import msg as this.$msg does not work here
      default: () => msg('modal.close')
    },
    ariaDescribedById: String,
    // whether or not this modal is inside yet another modal. Important to set to true so
    // that the overflow set on body is not removed when the child modal is closed
    isLoading: Boolean,
    notifyOutsideClick: Boolean,
    popoverMessage: String,
    popoverType: {
      type: String,
      default: 'success'
    },
    showClose: {
      type: Boolean,
      default: true
    }
  },
  provide: {
    // this is made available for all child components to know whether they are inside of a modal
    // or not.  This is used by this modal as well as menu-list so they know how to close when not
    // directly in the body
    isInModal: true
  },
  inject: {
    // This is set by the "provide" above if this modal is inside another modal.  Otherwise, false
    isInModal: { default: false }
  },
  components: { 'tt-popover': Popover },
  methods: {
    close() {
      this.$emit('close')
    },
    closePopover() {
      this.$emit('closePopover')
    },
    handleClick(e) {
      // if clicking outside of the container, then close it
      if (!this.$refs.container.contains(e.target) && !this.isExternalButtonClick(e) &&
        !this.isPopoverClick(e)) {
        this.notifyOutsideClick ? this.$emit('outsideClick') : this.close()
      }
    },
    isExternalButtonClick(e) {
      const externalBtns = this.$slots.externalButtons
      if (externalBtns) {
        for (let i = 0; i < externalBtns.length; i++) {
          const elem = externalBtns[i].elm
          if (elem && elem.nodeType === 1 && elem.contains(e.target)) {
            return true
          }
        }
      }
      return false
    },
    isPopoverClick(e) {
      const popover = this.$refs.popover
      return popover && popover.$el.contains(e.target)
    }
  },
  created() {
    if (!this.ariaLabel && !this.ariaLabeledById && process.env.NODE_ENV === 'development') {
      // tslint:disable:next-line no-console
      console.error('Must provide ariaLabel or ariaLabeledById')
    }
  },
  mounted() {
    // prevent scrollbars from showing up for underlying body and allow for smooth scrolling on iOS
    if (!this.isInModal) {
      disableBodyScroll(this.$el)
    }
    this.$refs.container.focus()
  },
  beforeDestroy() {
    // re-enable body scrolling that was disabled when the modal was opened
    if (!this.isInModal) {
      enableBodyScroll()
    }
  },
  watch: {
    popoverMessage() {
      if (this.popoverMessage) {
        domUtils.whenRefIsVisible(this.$refs, 'popover').then(() => {
          this.$refs.popover.$el.scrollIntoView(
            { behavior: 'smooth', block: 'start', inline: 'nearest' }
          )
        })
      }
    }
  }
}))
